import React from "react";
import { FontIcon, Spinner, SpinnerSize, Text, TooltipHost } from "@fluentui/react";
import { DragAndDropHelper } from "../../../../../../../utilities/DragAndDropHelper";
import { LanguageContext } from "../../../../../../../utilities/LocalizationModule";
import { dragZoneStyleSets } from "../../../../../config/controlStyles";
import { mobileListItemStyle } from "../../../../../styles/mobileStyles";
import { hasChildren, treeViewItemStyle } from "../../../../helpers/treeHelperGeneral";
import { ITessProjectFolderLocation } from "../../../../../services/TessService";
import { fontIconStyles } from "../../../../../styles/iconStyles";
import { ArrowClockwise20Regular } from "../../SvgIcons/ArrowClockwise20Regular";

interface ITessProjectFolderItemProps {
    item: ITessProjectFolderLocation;
    isMobile: boolean;
    allowScroll: boolean;
    onProjectFolderClick: (item: ITessProjectFolderLocation) => void;
    onExpandCollapseClick: (item: ITessProjectFolderLocation, isRefresh?: boolean) => Promise<void>
}

export const TessProjectFolderItem = ({ item, isMobile, allowScroll, onProjectFolderClick, onExpandCollapseClick }: ITessProjectFolderItemProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const [isDraggedOver, setIsDraggedOver] = React.useState(false);
    const dragCounter = React.useRef<number>(0);

    const dragEnter = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        if (DragAndDropHelper.validateDroppedFiles(event) || DragAndDropHelper.validateDroppedGridItems(event)) {
            event.preventDefault();
            event.stopPropagation();
            event.dataTransfer.dropEffect = "none";
            dragCounter.current = dragCounter.current + 1;
            setIsDraggedOver(true);
        }
    }, []);

    const dragOver = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = "none";
    }, []);

    const dragLeave = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.stopPropagation();
        if (isDraggedOver) {
            dragCounter.current = dragCounter.current - 1;
            if (!dragCounter.current || event.target === event.currentTarget) {
                setIsDraggedOver(false);
            }
        }
    }, [isDraggedOver]);

    const className = React.useMemo((): string => {
        let className: string = "list-item";
        if (item) {
            className += ` ${"tree-item"}`;
            if (item.isSelected) {
                className += ` ${"tree-item-selected"}`;
            }
        }

        if (isDraggedOver) {
            className += ` dropzone ${dragZoneStyleSets.dragzoneActive}`;
        }
        else {
            className += ` dropzone`;
        }
        return className;
    }, [item, isDraggedOver]);

    const showChevron = React.useMemo((): boolean => {
        return item.canExpand
            && !item.isLoading
            && !(item.loaded && !hasChildren(item));
    }, [item]);

    React.useEffect(() => {
        let debounceTimer;
        if (isDraggedOver && showChevron && !item.expanded && !item.isLoading) {
            debounceTimer = setTimeout(() => {
                onExpandCollapseClick(item, false);
            }, 1500);
        }

        return () => {
            clearTimeout(debounceTimer);
        };
    }, [item, isDraggedOver, showChevron, onExpandCollapseClick]);

    const folderStyles: React.CSSProperties = isMobile ? mobileListItemStyle : {};

    return (
        <div
            className={className}
            style={folderStyles}
            onClick={(e) => {
                e.stopPropagation();
                onProjectFolderClick(item);
            }}
            onDragEnter={dragEnter}
            onDragOver={dragOver}
            onDragLeave={dragLeave}>
            <div className={!allowScroll ? "list-item-link" : "list-item-link-scrollable"}>
                <div
                    style={treeViewItemStyle(item)}
                    onClick={(e) => {
                        if (showChevron) {
                            e.stopPropagation();
                            onExpandCollapseClick(item, false);
                        }
                    }}
                >
                    {item.isLoading && <Spinner size={SpinnerSize.xSmall} />}
                    {showChevron && <FontIcon iconName={item.expanded ? "ChevronDown" : "ChevronRight"} />}
                </div>
                <div className="list-item-icon">
                    <FontIcon iconName="ProjectCollection" className={fontIconStyles} style={{ color: "#0652dd" }} />
                </div>
                <Text className="list-item-name">
                    {item.name}
                </Text>
            </div>
            {item.canExpand && !item.isLoading && item.loaded && <TooltipHost content={languageStrings.Refresh}>
                <div className={isMobile ? "list-item-save-icon-mobile" : "list-item-save-icon"}>
                    {/* <FontIcon
                        iconName="Refresh"
                        className="refresh-icon"
                        onClick={async (e) => {
                            e.stopPropagation();
                            onExpandCollapseClick(item, true);
                        }}
                    /> */}
                    <ArrowClockwise20Regular
                        onClick={(e) => {
                            e.stopPropagation();
                            onExpandCollapseClick(item, true);
                        }}
                    />
                </div>
            </TooltipHost>}
        </div>
    )
};